import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Inbox } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
        navigate("/admin/login", { replace: true });
      }
    };
    checkAuth();
  }, [location.state, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/contact`, { withCredentials: true });
        setMessages(res.data);
      } catch {
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    };
    load();
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center">
        <p className="text-slate-400 text-sm tracking-widest uppercase">Memuat...</p>
      </div>
    );
  }
  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-navy-950" data-testid="admin-dashboard">
      <SEOHead title="Dashboard Admin | PT. Luminara Global Persada" description="Dashboard admin PT. Luminara Global Persada." />
      <header className="border-b border-white/10 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-mark-gold.png" alt="Logo PT. Luminara Global Persada" className="w-10 h-10 object-contain" />
            <div className="leading-tight">
              <p className="font-heading font-semibold text-sm text-white">Dashboard Admin</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-gold/80">PT. Luminara Global Persada</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            {user && (
              <div className="hidden sm:flex items-center gap-3">
                {user.picture && (
                  <img src={user.picture} alt={`Foto profil ${user.name}`} className="w-8 h-8 rounded-full border border-gold/40" referrerPolicy="no-referrer" />
                )}
                <div className="leading-tight text-right">
                  <p className="text-sm text-white" data-testid="admin-user-name">{user.name}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                </div>
              </div>
            )}
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="btn-outline !px-4 !py-2 !text-xs"
            >
              <LogOut size={14} /> Keluar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="kicker mb-3">Pesan Masuk</p>
            <h1 className="font-heading font-semibold text-2xl md:text-3xl text-white tracking-tight">
              Pesan Formulir Kontak
            </h1>
          </div>
          <p className="text-sm text-slate-500" data-testid="admin-message-count">
            {messages.length} pesan
          </p>
        </div>

        <div className="mt-10">
          {loadingMessages ? (
            <p className="text-slate-500 text-sm">Memuat pesan...</p>
          ) : messages.length === 0 ? (
            <div className="border border-white/10 bg-navy-900 p-14 text-center">
              <Inbox size={32} className="text-gold mx-auto" />
              <p className="mt-4 text-slate-400 text-sm">Belum ada pesan masuk.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((m, i) => (
                <article
                  key={m.id}
                  data-testid={`admin-message-${i}`}
                  className="border border-white/10 bg-navy-900 p-6 transition-colors duration-300 hover:border-gold/40"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <h2 className="font-heading font-semibold text-white">{m.nama}</h2>
                      <p className="text-xs text-slate-500 mt-1">
                        {m.perusahaan ? `${m.perusahaan} · ` : ""}
                        <a href={`mailto:${m.email}`} className="hover:text-gold transition-colors duration-300">{m.email}</a>
                        {" · "}{m.telepon}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-block text-[11px] font-bold tracking-widest text-gold border border-gold/40 px-2.5 py-1">
                        {m.jenis_kebutuhan}
                      </span>
                      <p className="text-[11px] text-slate-500 mt-2">
                        {new Date(m.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-300 leading-relaxed whitespace-pre-line">{m.pesan}</p>
                </article>
              ))}
            </div>
          )}
        </div>

        <Link to="/" data-testid="admin-back-to-site" className="mt-12 inline-block text-xs text-slate-500 hover:text-gold transition-colors duration-300">
          &larr; Kembali ke website
        </Link>
      </main>
    </div>
  );
}
